import React, { useState, useRef, useEffect, useCallback } from 'react';
import { LuPlay, LuPause, LuMic, LuMicOff, LuDownload } from 'react-icons/lu';
import './AudioPlayer.css';

/**
 * Áudio da conversa, no molde do WhatsApp: o player NÃO tem superfície própria
 * — ele vive dentro da bolha e tira as cores dela (`currentColor` para o traço
 * e a tinta translúcida para o trilho), senão um cartão claro dentro da bolha
 * verde fica estranho nos dois temas. Foi o que ele apontou em 23/09.
 *
 * Sem onda de amplitude de propósito: desenhar a onda exige baixar e decodificar
 * o arquivo (Web Audio), e onda inventada é decoração fingindo ser dado.
 */
interface AudioPlayerProps {
    src: string;
}

const formatarTempo = (t: number) => {
    if (!Number.isFinite(t) || t < 0) return '--:--';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src }) => {
    const [tocando, setTocando] = useState(false);
    const [posicao, setPosicao] = useState(0);
    const [duracao, setDuracao] = useState(NaN);
    const [falhou, setFalhou] = useState(false);
    const audioRef = useRef<HTMLAudioElement>(null);
    const trilhoRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const lerDuracao = () => {
            // Ogg do WhatsApp costuma chegar sem duração no contêiner: aí fica
            // Infinity/NaN e o rodapé mostra --:-- em vez de um 0:00 mentiroso.
            if (Number.isFinite(audio.duration) && audio.duration > 0) setDuracao(audio.duration);
        };
        const lerTempo = () => setPosicao(audio.currentTime);
        const aoTerminar = () => { setTocando(false); setPosicao(0); };
        const aoFalhar = () => { setFalhou(true); setTocando(false); };

        if (audio.readyState >= 1) lerDuracao();

        audio.addEventListener('loadedmetadata', lerDuracao);
        audio.addEventListener('durationchange', lerDuracao);
        audio.addEventListener('timeupdate', lerTempo);
        audio.addEventListener('ended', aoTerminar);
        audio.addEventListener('pause', () => setTocando(false));
        audio.addEventListener('play', () => setTocando(true));
        audio.addEventListener('error', aoFalhar);

        return () => {
            audio.removeEventListener('loadedmetadata', lerDuracao);
            audio.removeEventListener('durationchange', lerDuracao);
            audio.removeEventListener('timeupdate', lerTempo);
            audio.removeEventListener('ended', aoTerminar);
            audio.removeEventListener('error', aoFalhar);
        };
    }, []);

    const alternar = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) audio.play().catch(() => setFalhou(true));
        else audio.pause();
    };

    const irPara = useCallback((clientX: number) => {
        const audio = audioRef.current;
        const trilho = trilhoRef.current;
        if (!audio || !trilho || !Number.isFinite(duracao)) return;
        const r = trilho.getBoundingClientRect();
        const razao = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
        audio.currentTime = razao * duracao;
        setPosicao(razao * duracao);
    }, [duracao]);

    const aoTeclar = (e: React.KeyboardEvent) => {
        const audio = audioRef.current;
        if (!audio) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const passo = e.key === 'ArrowRight' ? 5 : -5;
            audio.currentTime = Math.max(0, audio.currentTime + passo);
            setPosicao(audio.currentTime);
        }
    };

    if (falhou) {
        return (
            <span className="ch-midia-off">
                <LuMicOff size={15} />
                Áudio indisponível
            </span>
        );
    }

    const pct = Number.isFinite(duracao) && duracao > 0 ? (posicao / duracao) * 100 : 0;
    // Parado mostra a duração; tocando, o quanto já correu — como no WhatsApp.
    const tempo = tocando || posicao > 0 ? formatarTempo(posicao) : formatarTempo(duracao);

    return (
        <div className="ch-au">
            <audio ref={audioRef} src={src} preload="metadata" />

            <button
                type="button"
                className="ch-au-play"
                onClick={alternar}
                aria-label={tocando ? 'Pausar áudio' : 'Tocar áudio'}
            >
                {tocando ? <LuPause size={15} /> : <LuPlay size={15} />}
            </button>

            <div className="ch-au-corpo">
                <div
                    ref={trilhoRef}
                    className="ch-au-trilho"
                    role="slider"
                    tabIndex={0}
                    aria-label="Posição do áudio"
                    aria-valuemin={0}
                    aria-valuemax={Number.isFinite(duracao) ? Math.round(duracao) : 0}
                    aria-valuenow={Math.round(posicao)}
                    aria-valuetext={formatarTempo(posicao)}
                    onPointerDown={e => irPara(e.clientX)}
                    onKeyDown={aoTeclar}
                >
                    <span className="ch-au-feito" style={{ width: `${pct}%` }} />
                    <span className="ch-au-bolinha" style={{ left: `${pct}%` }} />
                </div>

                <div className="ch-au-rod">
                    <LuMic size={12} />
                    <span>{tempo}</span>
                </div>
            </div>

            <a
                className="ch-au-baixar"
                href={src}
                download
                target="_blank"
                rel="noopener noreferrer"
                title="Baixar áudio"
                aria-label="Baixar áudio"
            >
                <LuDownload size={14} />
            </a>
        </div>
    );
};

export default AudioPlayer;
